import { Autorenew } from '@mui/icons-material';
import {
  IconButton, IconButtonProps, Theme, Tooltip, TooltipProps, useTheme,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import OkCancelConfirmDialog from '../ConfirmDialog/OkCancelConfirmDialog';
import YesNoConfirmDialog from '../ConfirmDialog/YesNoConfirmDialog';
import CommonButtonProps from './ETOButtonCommonProps';
import commonButtonStyles from './ETOButtonCommonStyles';
import IconWrapper from './IconWrapper';

const colorSx = (
  theme: Theme,
  color: 'primary' | 'secondary' | 'warning' | 'error' | 'success' | 'grey',
) => {
  switch (color) {
    case 'primary':
      return { color: theme.palette.primary.main };
    case 'secondary':
      return { color: theme.palette.secondary.main };
    case 'warning':
      return { color: theme.palette.warning.main };
    case 'error':
      return { color: theme.palette.error.main };
    case 'success':
      return { color: theme.palette.success.main };
    case 'grey':
      return { color: theme.palette.grey[500] };
    default:
      return { color: theme.palette.primary.main };
  }
};

const disabledSx = (theme: Theme) => ({
  backgroundColor: theme.palette.grey[300],
  color: theme.palette.getContrastText(theme.palette.grey[300]),
  '&:hover': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.getContrastText(theme.palette.grey[300]),
  },
});

const sizeSx = (theme: Theme, size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return { textTransform: 'capitalize', width: theme.spacing(4), height: theme.spacing(4) };
    case 'medium':
      return { textTransform: 'capitalize', width: theme.spacing(4.5), height: theme.spacing(4.5) };
    case 'large':
      return { textTransform: 'capitalize', width: theme.spacing(6), height: theme.spacing(6) };
    default:
      return { textTransform: 'capitalize', width: theme.spacing(4.5), height: theme.spacing(4.5) };
  }
};

export interface ETOIconButtonProps extends CommonButtonProps {
  buttonProps?: Omit<IconButtonProps, 'color' | 'size'>;
  tooltipProps?: Omit<TooltipProps, 'children'>;
}

const ETOIconButton: React.FC<React.PropsWithChildren<ETOIconButtonProps>> = (props) => {
  const {
    loading,
    children,
    customSx,
    disabled,
    color,
    size,
    buttonProps,
    onClick,
    confirm,
    tooltipProps,
  } = props;
  const theme = useTheme();
  const commonStyles = commonButtonStyles(theme);
  const [dialogOpen, setDialogOpen] = useState(false);

  const _onDialogLeft = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => {
      setDialogOpen(false);
      if (confirm && confirm.type === 'yesNo') {
        confirm.onNo(e);
      } else if (confirm && confirm.type === 'custom') {
        confirm.leftButton?.onClick(e, undefined);
      }
    },
    [confirm],
  );

  const _onDialogRight = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => {
      if (onClick) {
        onClick(e);
        setDialogOpen(false);
      }
    },
    [onClick],
  );

  const _renderConfirm = useCallback(() => {
    if (confirm) {
      switch (confirm.type) {
        case 'yesNo':
          return (
            <YesNoConfirmDialog
              open={dialogOpen}
              title={confirm.title}
              content={confirm.content}
              data={confirm.data}
              onYes={_onDialogRight}
              onNo={_onDialogLeft}
            />
          );
        case 'okCancel':
          return (
            <OkCancelConfirmDialog
              open={dialogOpen}
              title={confirm.title}
              content={confirm.content}
              data={confirm.data}
              onOk={_onDialogRight}
              onCancel={() => _onDialogLeft(undefined)}
            />
          );
        case 'custom':
          // eslint-disable-next-line react/jsx-props-no-spreading
          return <ConfirmDialog {...confirm} open={dialogOpen} />;
        default:
          // eslint-disable-next-line react/jsx-no-useless-fragment
          return <></>;
      }
    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }, [_onDialogLeft, _onDialogRight, confirm, dialogOpen]);

  const _onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => {
      if (onClick) {
        if (confirm) {
          setDialogOpen(true);
        } else {
          onClick(e);
        }
      }
    },
    [onClick, setDialogOpen, confirm],
  );

  const button = useMemo(() => (
    <IconButton
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buttonProps}
      disabled={disabled || loading}
      sx={[
        { ...(!loading && (disabled ? disabledSx : colorSx(theme, color))) },
        sizeSx(theme, size),
        { ...commonStyles[size] },
        ...(Array.isArray(customSx) ? customSx : [customSx]),
      ]}
      onClick={(e) => _onClick(e)}
      data-testid="icon-button"
      tabIndex={0}
      size={size}
    >
      <IconWrapper size={size} loading={loading}>
        {loading ? <Autorenew /> : children}
      </IconWrapper>
    </IconButton>
  ), [loading, children, size, disabled, color, onClick, buttonProps, theme]);

  const element = useMemo(() => ((tooltipProps?.title && !(disabled || loading)) ? (
    <Tooltip {...{ leaveTouchDelay: 0, ...tooltipProps }}>{button}</Tooltip>
  ) : button), [tooltipProps?.title, disabled, loading, button]);

  return (
    <>
      {element}
      {confirm && _renderConfirm()}
    </>
  );
};

export default ETOIconButton;
